import { HTMLAttributes, ReactNode } from 'react'
import './Alert.css'

export type AlertVariant = 'info' | 'success' | 'warning' | 'danger'

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  /** Alert type/severity */
  variant: AlertVariant
  /** Alert title (optional) */
  title?: string
  /** Dismiss button handler - if provided, shows dismiss button */
  onDismiss?: () => void
  /** Children content */
  children: ReactNode
}

const icons: Record<AlertVariant, string> = {
  info: 'ℹ️',
  success: '✓',
  warning: '⚠',
  danger: '✕',
}

/**
 * Alert component for notifications and messages
 * Derek-friendly: clear visual distinction, high contrast
 */
export default function Alert({
  variant,
  title,
  onDismiss,
  children,
  className = '',
  ...props
}: AlertProps) {
  const alertClasses = ['ps-alert', `ps-alert--${variant}`, className]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={alertClasses} role="alert" {...props}>
      <span className="ps-alert__icon" aria-hidden="true">
        {icons[variant]}
      </span>
      <div className="ps-alert__content">
        {title && <strong className="ps-alert__title">{title}</strong>}
        <div className="ps-alert__message">{children}</div>
      </div>
      {onDismiss && (
        <button
          type="button"
          className="ps-alert__dismiss"
          onClick={onDismiss}
          aria-label="Dismiss alert"
        >
          ×
        </button>
      )}
    </div>
  )
}
